import React, { useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useMutation } from 'redux-query-react'

import { AvField, AvForm } from 'availity-reactstrap-validation'
import { Link, Redirect, useHistory } from 'react-router-dom'
import { Button, Card, CardBody, Col, Fade, Row, UncontrolledAlert } from 'reactstrap'

import { getActiveUser, justReplace, getClients } from 'app/selectors'
import logo from 'assets/images/logo-blue.png'
import { SmartFormServerErrors } from 'components/FormServerErrors'
import { PostalCodeField } from 'components/PostalCodeField'
import { StateProvinceSelect } from 'components/ProvinceSelect'
import { Errors, useFormServerErrors } from 'hooks/useFormServerErrors'
import { useTranslation } from 'react-i18next'
import { Client, updateAccount, User } from 'api'
import { logout } from 'app/authReducer'
import { isReleased } from 'utilities'

export const PersonalVerification = () => {
  const history = useHistory()

  const clients: Client[] = useSelector(getClients) || []
  const [serverErrors, setServerErrors] = useState<Errors>()
  const { handleFieldChange, validate } = useFormServerErrors(serverErrors, setServerErrors)
  const [success, setSuccess] = useState(false)
  const activeUser = useSelector(getActiveUser)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const formRef = useRef<AvForm>()

  const [mutationState, saveChanges] = useMutation((user: User) => {
    return updateAccount(
      {
        account: { user: user, clients: clients },
      },
      {
        force: true,
        transform: (body) => {
          return { account: body.user }
        },
        update: {
          account: justReplace,
        },
      },
    )
  })

  if (!isReleased('personal_verification_for_existing_users')) {
    return <Redirect to="/" />
  }

  function handleValidSubmit(_event, values) {
    saveChanges(values.user)?.then((response) => {
      let errorMsg = ''
      if (response?.body?.messages) {
        setServerErrors(response.body.messages)
      } else if (response?.text) {
        const match = response.text.match(/^[A-Za-z]+Error.*\n=+\n\n([^\n]+)/)
        if (match) {
          errorMsg = match[1]
        } else {
          errorMsg = 'Unknown error, please contact support.'
        }
        setServerErrors({ base: [errorMsg] })
      } else {
        setServerErrors({ base: ['An unknown error occurred.'] })
      }

      if (response?.body?.status < 400) {
        setSuccess(true)
        history.push('/')
      }
    })
  }

  const isPending = mutationState.isPending

  return (
    <>
      <div className="d-flex justify-content-between pr-1">
        <div></div>
        <Link to="/" className="logo text-center logo-light non-menu-logo">
          <span className="logo logo-lg non-menu-logo">
            <img src={logo} alt="logo" height="20" />
          </span>
        </Link>
        <Button className="btn-rotessa-tertiary" onClick={() => dispatch(logout())}>
          Logout
        </Button>
      </div>

      <Row className="ml-1 mr-1 mb-4">
        <Col xl={{ size: 4, offset: 4 }} lg={{ size: 6, offset: 3 }} md={{ size: 8, offset: 2 }}>
          <div>
            In order to keep your account in compliance with <b>regulatory changes</b>, please ensure the following
            information is accurate and up-to-date.
          </div>
          <div className="page-title-box">
            <h1 className="page-title">Verify Personal Information</h1>
          </div>
          <Fade in={success} mountOnEnter={true} unmountOnExit={true}>
            <UncontrolledAlert>Update Successful</UncontrolledAlert>
          </Fade>
          <SmartFormServerErrors errors={serverErrors} avFormRef={formRef} />
          <Fade>
            <AvForm disabled={isPending} onValidSubmit={handleValidSubmit} ref={formRef} model={{ user: activeUser }}>
              <Card>
                <CardBody>
                  <h4 className="header-title mb-3">Profile</h4>
                  <Row>
                    <Col>
                      <AvField
                        name="user.firstName"
                        label="First Name"
                        type="text"
                        placeholder="John"
                        required
                        onChange={handleFieldChange}
                        validate={{
                          async: validate,
                          required: {
                            value: true,
                            errorMessage: t('required_field'),
                          },
                        }}
                      />
                    </Col>
                    <Col>
                      <AvField
                        name="user.lastName"
                        label="Last Name"
                        type="text"
                        placeholder="Smith"
                        required
                        onChange={handleFieldChange}
                        validate={{
                          async: validate,
                          required: {
                            value: true,
                            errorMessage: t('required_field'),
                          },
                        }}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <AvField
                        name="user.dateOfBirth"
                        type="date"
                        label="Date of Birth"
                        required
                        validate={{
                          async: validate,
                          required: {
                            value: true,
                            errorMessage: t('required_field'),
                          },
                          dateRange: {
                            start: { value: -200, units: 'years' },
                            end: { value: 0, units: 'days' },
                            errorMessage: 'Date of birth must in the past',
                          },
                        }}
                        onChange={handleFieldChange}
                      />
                    </Col>
                    <Col>
                      <AvField
                        name="user.occupation"
                        label="Occupation"
                        type="text"
                        placeholder="Ex: Bookkeeper"
                        required
                        onChange={handleFieldChange}
                        validate={{
                          async: validate,
                          required: {
                            value: true,
                            errorMessage: t('required_field'),
                          },
                        }}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <Row>
                    <Col md={9}>
                      <h4 className="header-title mb-3">Address</h4>
                    </Col>
                  </Row>
                  <Row>
                    <AvField
                      validate={{ async: validate }}
                      onChange={handleFieldChange}
                      name="user.addressAttributes.id"
                      type="hidden"
                    />
                    <Col lg={6}>
                      <AvField
                        onChange={handleFieldChange}
                        name="user.addressAttributes.address1"
                        label="Street"
                        type="text"
                        placeholder="Ex. 123 Main St"
                        validate={{
                          required: {
                            value: true,
                            errorMessage: t('required_field'),
                          },
                          async: validate,
                        }}
                      />
                    </Col>
                    <Col lg={6}>
                      <AvField
                        onChange={handleFieldChange}
                        name="user.addressAttributes.city"
                        label="City"
                        type="text"
                        placeholder="Enter the town or city"
                        validate={{
                          required: {
                            value: true,
                            errorMessage: t('required_field'),
                          },
                          async: validate,
                        }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={6}>
                      <StateProvinceSelect
                        name="user.addressAttributes.provinceCode"
                        countryCode={'ALL'}
                        serverErrors={serverErrors}
                        required={true}
                        placeholder="Choose one..."
                      />
                    </Col>
                    <Col lg={6}>
                      <PostalCodeField
                        required={true}
                        name="user.addressAttributes.postalCode"
                        countryCode="ALL"
                        placeholder="Ex. A1A 1A1"
                        onChange={handleFieldChange}
                        serverErrors={serverErrors}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <Row>
                    <Col md={12}>
                      <div className="mb-3">
                        <h4 className="header-title">
                          {activeUser?.rolesAttributes?.length > 1 ? 'Businesses' : 'Business'}
                        </h4>
                        <span>Declare your position for each of the following businesses you are involved with.</span>
                      </div>
                    </Col>
                  </Row>
                  {activeUser.rolesAttributes?.map((position, idx) => (
                    <Row key={position.id}>
                      <Col lg={12}>
                        <AvField
                          onChange={handleFieldChange}
                          name={`user.rolesAttributes[${idx}].position`}
                          type="text"
                          label={`Position - ${position.clientName} ${
                            position.status === 'Closed' ? `(${position.status})` : ''
                          }`}
                          placeholder="Ex: Bookkeeper"
                          validate={{
                            required: {
                              value: true,
                              errorMessage: t('required_field'),
                            },
                            async: validate,
                          }}
                        />
                        <AvField name={`user.rolesAttributes[${idx}].id`} type="hidden" value={position.id} />
                      </Col>
                    </Row>
                  ))}
                </CardBody>
              </Card>

              <div className="text-right">
                <Button disabled={isPending} color="primary" className="mb-2 ml-1">
                  Submit
                </Button>
              </div>
            </AvForm>
          </Fade>
        </Col>
      </Row>
    </>
  )
}
